// D1 (durability audit): derives a run's DISPLAY status from its Firestore
// fields. "Interrupted" is never written by any client: the client that
// owned an interrupted run is, by definition, the one whose tab closed, so
// nothing ever comes back to mark it. There is also no backend orchestrator
// for runs (unlike dft-service/qe-service/lattice-service's job docs) to do
// it server-side either. So detection is entirely read-side: a run doc whose
// status is still 'running' with a stale updated_at is presented as
// 'interrupted', with whatever partial results (toolResults/candidateCount)
// were persisted before the tab died.
//
// STALE_AFTER_MS loosely mirrors dft-service's convention
// (models/dft-service/persistence.py: STALE_AFTER = 600s, 5x its ~120s
// heartbeat) but widened: our "heartbeat" is a per-step Firestore write, not
// a fixed-interval poll, and an individual MatterGen/FM4M/GT4SD step can
// legitimately run for minutes (D4's own audit language: MatterGen server
// compute "may continue for up to an hour"), so a fixed multi-minute silence
// alone isn't proof of death the way it is for dft-service's fixed-cadence
// heartbeat. 15 minutes is wide enough that a slow-but-alive step's silence
// doesn't false-positive, narrow enough to flag a genuinely abandoned tab
// well within a normal session.
export const STALE_AFTER_MS = 15 * 60 * 1000;

/** Coerce a Firestore Timestamp, a {seconds,nanoseconds} plain object (what a
 * Firestore Timestamp looks like once round-tripped through JSON in a test
 * fixture), or an ISO string into epoch milliseconds. Returns null for
 * anything unparsable: callers must treat that as "no trustworthy signal",
 * never as epoch 0. */
function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Returns one of: 'complete' | 'running' | 'interrupted' | 'error'.
 *
 * A run doc with no status field at all (every run written before D1, and
 * pushRun()'s still-atomic investigate-mode writes) is treated as
 * 'complete': the same, unambiguous default it always implicitly had, and
 * the only value that renders exactly as it always has.
 */
export function deriveRunStatus(run, { now = Date.now(), staleAfterMs = STALE_AFTER_MS } = {}) {
  const status = run?.status;
  if (status !== 'running') return status || 'complete';
  const updatedMs = toMillis(run?.updatedAt) ?? toMillis(run?.ts);
  if (updatedMs == null) return 'interrupted'; // no timestamp to trust at all -> assume dead
  return (now - updatedMs) > staleAfterMs ? 'interrupted' : 'running';
}

// D8 (durability audit 2026-08-20): a draft run doc (the plan-review window's
// own durable record, written the moment a plan is produced, before Generate
// is ever clicked) has no backend orchestrator either, and nobody ever
// deletes an abandoned one -- the user just never came back to it. Rather
// than a scheduled prune, this is a read-side "hide it" rule, the same shape
// deriveRunStatus's own 'interrupted' detection already uses: a draft older
// than STALE_DRAFT_AFTER_MS is treated as abandoned and filtered out of
// History by the caller (see HistoryTable.jsx), never deleted, so a
// synced-later Firestore read still finds it if the threshold logic ever
// changes. Only meaningful for status 'draft'; any other status returns
// false unconditionally, ts-less drafts count as stale (no trustworthy
// signal, same "assume dead" default deriveRunStatus uses for a missing
// updatedAt).
export const STALE_DRAFT_AFTER_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function isStaleDraft(run, { now = Date.now() } = {}) {
  if (run?.status !== 'draft') return false;
  const tsMs = toMillis(run?.ts);
  if (tsMs == null) return true;
  return (now - tsMs) > STALE_DRAFT_AFTER_MS;
}
